/**
 * @ngdoc overview
 * @name searchconsole.service:SearchConsoleService
 */
'use strict';
define(['app'], function (app) {
    app.service('SearchConsoleService', SearchConsoleService);
    SearchConsoleService.$inject = ['API', '__env', '$window'];

    function SearchConsoleService(API, __env, $window) {
        var apiBaseUrl = __env.apiGateway + '/api/' + __env.apiVersion + '/';
        var apiPath = apiBaseUrl + __env.searchConsoleAPI;
        var apiV2Path = __env.apiGateway + '/api/' + (__env.apiVersion2 || 'v2') + '/search-console';

        function markConnectionChanged() {
            $window.localStorage.setItem('searchConsoleProjectConnection', String(Date.now()));
        }

        function unwrapV2(response) {
            var body = response && response.data !== undefined ? response.data : response;
            if (body && body.error) {
                throw new Error(body.error.message || 'Search Console request failed');
            }
            var data = body && body.data !== undefined ? body.data : body;
            if (data && data.error) {
                throw new Error(data.error.message || 'Search Console request failed');
            }
            return data;
        }

        this.generateProjectAuthUrl = function (projectId, options) {
            var write = options && options.write ? '&write=true' : '';
            return API.get(apiV2Path + '/auth-url?projectId=' + encodeURIComponent(projectId) + write, true)
                .then(function (response) {
                    return unwrapV2(response);
                });
        };

        this.generateDraftAuthUrl = function () {
            return API.get(apiV2Path + '/draft/auth-url', true)
                .then(function (response) {
                    return unwrapV2(response);
                });
        };

        this.completeDraftConnection = function (code, state) {
            return API.post(apiV2Path + '/draft/exchange', {code: code, state: state}, true)
                .then(function (response) {
                    return unwrapV2(response);
                });
        };

        this.connectDraftToProject = function (draftId, projectId, propertyUrl) {
            return API.post(
                apiV2Path + '/draft/' + encodeURIComponent(draftId) + '/connect',
                {projectId: projectId, propertyUrl: propertyUrl},
                true,
            ).then(function (response) {
                var data = unwrapV2(response);
                markConnectionChanged();
                return data;
            });
        };

        this.completeProjectConnection = function (code, state) {
            return API.post(apiV2Path + '/exchange', {code: code, state: state}, true)
                .then(function (response) {
                    var data = unwrapV2(response);
                    markConnectionChanged();
                    return data;
                });
        };

        this.getProjectConnection = function (projectId) {
            return API.get(apiV2Path + '/projects/' + projectId, true)
                .then(function (response) {
                    return unwrapV2(response);
                });
        };

        this.getProjectConnections = function (projectIds) {
            return API.post(apiV2Path + '/projects/status', {projectIds: projectIds || []}, true)
                .then(function (response) {
                    return unwrapV2(response) || [];
                });
        };

        this.getProjectProperties = function (projectId) {
            return API.get(apiV2Path + '/projects/' + projectId + '/properties', true)
                .then(function (response) {
                    return unwrapV2(response);
                });
        };

        this.getProjectSitemaps = function (projectId) {
            return API.get(apiV2Path + '/projects/' + projectId + '/sitemaps', true)
                .then(function (response) {
                    return unwrapV2(response);
                });
        };

        this.submitProjectSitemap = function (projectId, path) {
            return API.post(apiV2Path + '/projects/' + projectId + '/sitemaps', {path: path}, true)
                .then(function (response) {
                    return unwrapV2(response);
                });
        };

        this.connectProjectProperty = function (projectId, propertyUrl) {
            return API.put(apiV2Path + '/projects/' + projectId + '/property', {propertyUrl: propertyUrl}, true)
                .then(function (response) {
                    var data = unwrapV2(response);
                    markConnectionChanged();
                    return data;
                });
        };

        this.disconnectProject = function (projectId) {
            return API.delete(apiV2Path + '/projects/' + projectId, null, true)
                .then(function (response) {
                    var data = unwrapV2(response);
                    markConnectionChanged();
                    return data;
                });
        };

        this.getProjectSummary = function (projectId, filters) {
            var params = [];
            filters = filters || {};
            if (filters.startDate) params.push('startDate=' + encodeURIComponent(filters.startDate));
            if (filters.endDate) params.push('endDate=' + encodeURIComponent(filters.endDate));
            if (filters.country) params.push('country=' + encodeURIComponent(filters.country));
            if (filters.device) params.push('device=' + encodeURIComponent(filters.device));
            if (filters.query) params.push('query=' + encodeURIComponent(filters.query));
            if (filters.page) params.push('page=' + encodeURIComponent(filters.page));
            if (filters.searchAppearance) params.push('searchAppearance=' + encodeURIComponent(filters.searchAppearance));
            if (filters.searchType) params.push('searchType=' + encodeURIComponent(filters.searchType));
            var query = params.length ? '?' + params.join('&') : '';
            return API.get(apiV2Path + '/projects/' + projectId + '/summary' + query, true)
                .then(function (response) {
                    return unwrapV2(response);
                });
        };

        this.getProjectFilters = function (projectId, filters) {
            filters = filters || {};
            var query = filters.searchType ? '?searchType=' + encodeURIComponent(filters.searchType) : '';
            return API.get(apiV2Path + '/projects/' + projectId + '/filters' + query, true)
                .then(function (response) {
                    return unwrapV2(response);
                });
        };

        this.getProjectPerformance = function (projectId, dimension, startDate, endDate, startRow, filters) {
            filters = filters || {};
            return API.post(apiV2Path + '/projects/' + projectId + '/performance', {
                dimension: dimension,
                startDate: startDate,
                endDate: endDate,
                startRow: startRow || 0,
                rowLimit: filters.rowLimit || 25,
                sortBy: filters.sortBy || 'clicks',
                sortDirection: filters.sortDirection || 'desc',
                search: filters.search || null,
                country: filters.country || null,
                device: filters.device || null,
                query: filters.query || null,
                page: filters.page || null,
                searchAppearance: filters.searchAppearance || null,
                searchType: filters.searchType || 'web'
            }, true).then(function (response) {
                return unwrapV2(response) || [];
            });
        };

        this.generateAuthUrl = function () {
            return API.get(apiPath + '/auth-url', true, true);
        };

        this.getToken = function (code) {
            return API.get(apiPath + '/token?code=' + code, true);
        };

        this.getSites = function (token) {
            return API.get(apiPath + '/sites?token=' + token, true);
        };

        this.getQueries = function (token, filters) {
            return API.get(apiPath + '/queries?token=' + token + '&filters=' + JSON.stringify(filters), true);
        };

        this.getPerformanceSummary = function (token, siteUrl) {
            return API.post(apiPath + '/summary', {token: token, siteUrl: siteUrl});
        };

        this.getPerformanceBreakdown = function (token, siteUrl, dimension, startDate, endDate, startRow) {
            return API.post(apiPath + '/performance', {token: token, siteUrl: siteUrl, dimension: dimension,
                startDate: startDate, endDate: endDate, startRow: startRow || 0});
        };

        this.findMatchingProperty = function (projectUrl, sites) {
            if (!projectUrl || !Array.isArray(sites)) return null;
            var project;
            try {
                project = new URL(/^https?:\/\//i.test(projectUrl) ? projectUrl : 'https://' + projectUrl);
            } catch (e) {
                return null;
            }
            var matches = sites.filter(function (site) {
                if (!site || !site.siteUrl) return false;
                if (site.siteUrl.indexOf('sc-domain:') === 0) {
                    var domain = site.siteUrl.slice('sc-domain:'.length).toLowerCase();
                    return project.hostname === domain || project.hostname.slice(-(domain.length + 1)) === '.' + domain;
                }
                try {
                    var property = new URL(site.siteUrl);
                    return project.origin === property.origin &&
                        (project.pathname + '/').indexOf(property.pathname.replace(/\/$/, '') + '/') === 0;
                } catch (e) {
                    return false;
                }
            });
            matches.sort(function (a, b) {
                var aPrefix = a.siteUrl.indexOf('sc-domain:') === 0 ? 0 : a.siteUrl.length;
                var bPrefix = b.siteUrl.indexOf('sc-domain:') === 0 ? 0 : b.siteUrl.length;
                return bPrefix - aPrefix;
            });
            return matches.length ? matches[0].siteUrl : null;
        };

        this.getProjectProperty = function (projectId, workspaceId, projectUrl, sites) {
            if (!Array.isArray(sites)) return null;
            var saved = $window.localStorage.getItem('searchConsoleProjectProperty:' + workspaceId + ':' + projectId);
            if (saved && sites.some(function (site) { return site && site.siteUrl === saved; })) return saved;
            return this.findMatchingProperty(projectUrl, sites);
        };

        this.saveProjectProperty = function (projectId, workspaceId, siteUrl) {
            $window.localStorage.setItem('searchConsoleProjectProperty:' + workspaceId + ':' + projectId, siteUrl);
            $window.localStorage.setItem('searchConsoleProjectConnection', String(Date.now()));
        };

        this.connectProject = function (project, options) {
            var pendingProject = {
                id: project.id,
                workspaceId: project.workspaceId,
                name: project.name,
                url: project.url
            };
            if (options && options.returnState) {
                pendingProject.returnState = options.returnState;
                pendingProject.returnParams = options.returnParams || {};
            }
            $window.localStorage.setItem('searchConsolePendingProject', JSON.stringify(pendingProject));
            return this.generateProjectAuthUrl(project.id, options).then(function (data) {
                var url = data && data.url;
                if (!url) throw new Error('Search Console authorization URL unavailable');
                $window.location.href = url;
            }).catch(function (error) {
                $window.localStorage.removeItem('searchConsolePendingProject');
                throw error;
            });
        };
    }
});
